import React, { useEffect, useMemo, useState } from "react";

/**
 * Wave C · "you're in" confetti (Vania 7 Oct 2026).
 *
 * Falls once over ~1.2s. Small diamonds, dots and short bars in the four
 * iko colours (pink / purple / blue / green). No yellow pieces, no emoji,
 * no bounce. Respects prefers-reduced-motion (returns null · the yellow
 * screen bar handles the celebration on its own).
 *
 * Works on both themes because the pieces float above the ground via a
 * portal-free fixed overlay (CSS tokens not required — the colours are
 * iko-exact hexes which read correctly on cream + night).
 */
const IKO = ["#FF6B9D", "#9D4EDD", "#5390FF", "#44DD88"];
const SHAPES = ["diamond", "dot", "bar"];
const PIECE_COUNT = 36;

function reducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return !!window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function Confetti({ run = false, durationMs = 1200 }) {
  const [alive, setAlive] = useState(false);
  useEffect(() => {
    if (!run) return;
    if (reducedMotion()) return;
    setAlive(true);
    const t = setTimeout(() => setAlive(false), durationMs + 150);
    return () => clearTimeout(t);
  }, [run, durationMs]);

  const pieces = useMemo(() => {
    if (!alive) return [];
    const out = [];
    for (let i = 0; i < PIECE_COUNT; i++) {
      const color = IKO[i % IKO.length];
      const shape = SHAPES[i % SHAPES.length];
      const left = Math.random() * 100;
      const delay = Math.random() * 120;
      const drift = (Math.random() - 0.5) * 40;
      const rot = Math.random() * 360;
      const size = shape === "bar" ? 10 : shape === "diamond" ? 8 : 6;
      out.push({ i, color, shape, left, delay, drift, rot, size });
    }
    return out;
  }, [alive]);

  if (!alive) return null;

  return (
    <div data-testid="confetti" aria-hidden="true"
         style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 64,
                  overflow: "hidden" }}>
      <style>{`
        @keyframes ww-confetti-fall {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 1; }
          100% { transform: translate3d(var(--dx), 100vh, 0) rotate(var(--r)); opacity: 0.9; }
        }
      `}</style>
      {pieces.map((p) => {
        const style = {
          position: "absolute",
          top: "-10vh",
          left: `${p.left}%`,
          width: p.shape === "bar" ? 3 : p.size,
          height: p.shape === "bar" ? p.size : p.size,
          background: p.color,
          borderRadius: p.shape === "dot" ? 999 : p.shape === "bar" ? 2 : 0,
          transform: p.shape === "diamond" ? "rotate(45deg)" : "none",
          animation: `ww-confetti-fall ${durationMs}ms linear ${p.delay}ms forwards`,
          "--dx": `${p.drift}vw`,
          "--r": `${p.rot}deg`,
        };
        return <span key={p.i} data-testid={`confetti-piece-${p.i}`} style={style} />;
      })}
    </div>
  );
}
